import 'whatwg-fetch';
import 'custom-event-polyfill';

import Auth0Lock from 'auth0-lock';
import Autofocuser from './autofocuser';
import ErrorLogger from './ErrorLogger';
import LoginView from './LoginView';
import addSubLogo from './headingLogos/logo';
import { EmailFirst } from './client/EmailFirst';
import { form } from 'formifier';
import { formErrors } from './errorMessages/formErrors';
import HidableElement from './HidableElement';
import LockWrapper from './lockWrapper/lockWrapper';
import { Renderer, AUTOFOCUS_CALLBACK } from './Renderer';
import TextContainerElement from './TextContainerElement';
import TopLevelErrorHandler from './TopLevelErrorHandler';
import topLevelErrorRenderer from './TopLevelErrorRenderer';
import txid from './txid';
import { analytics, ELEMENT_CLICKED, ELEMENT_CLICKED_PROPERTIES } from './lib';
import {
  TextMapping,
  VisibilityMapping,
  WrappedElement,
} from './WrappedElement';

const TXID = txid();

const QUERY_PARAM_TO_LOCK_SCREEN_MAP = {
  'sign-up': 'signUp',
  'sign-in': 'login',
  'sign-in-sso': 'sso',
};

const WRONG_PASSWORD_ERROR_MESSAGE =
  "We weren't able to sign you in.  Double-check your email and password, or sign in with SSO.";

const INVALID_SIGNUP_ERROR_MESSAGE =
  "We weren't able to create your account. Sign in (see link below) with an existing account or try again later.";

const SMARTDOLLAR_ACCOUNT_CREATE_URL =
  'https://www.ramseysolutions.com/corporate-wellness/smartdollar/sign-in-or-create-account';

const overrideSmartDollarCreateAccount = (appName, wrappedElements) => {
  if (appName === 'SmartDollar') {
    wrappedElements.showSignUpFormButton.element.href = SMARTDOLLAR_ACCOUNT_CREATE_URL;
  }
};

const initConfiguration = () => {
  // Decode utf8 characters properly
  const parsedConfig = JSON.parse(
    decodeURIComponent(escape(window.atob(AUTH0_CONFIGURATION)))
  );

  parsedConfig.extraParams = parsedConfig.extraParams || {};

  return parsedConfig;
};

const maintenanceModeEnabled = MAINTENANCE_MODE_ENABLED === 'true';

const initOptions = (configuration) => ({
  container: 'auth0',
  auth: {
    redirectUrl: configuration.callbackURL,
    responseType: 'code',
    params: configuration.internalOptions,
  },
  configurationBaseUrl: configuration.clientConfigurationBaseUrl,
  overrides: {
    __tenant: configuration.auth0Tenant,
    __token_issuer: configuration.authorizationServer.issuer,
  },
  allowedConnections: configuration.connection
    ? [configuration.connection]
    : null,
  rememberLastLogin: false,
  languageDictionary: {
    emailInputPlaceholder: '',
    error: {
      login: {
        'lock.invalid_email_password': WRONG_PASSWORD_ERROR_MESSAGE,
        password_change_required: WRONG_PASSWORD_ERROR_MESSAGE,
      },
      signUp: {
        user_exists: INVALID_SIGNUP_ERROR_MESSAGE,
        username_exists: INVALID_SIGNUP_ERROR_MESSAGE,
        'lock.fallback': INVALID_SIGNUP_ERROR_MESSAGE,
      },
    },
    forgotPasswordAction: '',
    forgotPasswordInstructions: '',
    passwordInputPlaceholder: '',
    blankPasswordErrorHint: 'Please enter your password.',
    signUpTitle: 'Create Account',
    signUpLabel: 'Create Account',
    signUpSubmitLabel: 'Create Account',
    forgotPasswordSubmitLabel: 'Send Password Reset Email',
    loginAtLabel: 'Sign in at %s',
    loginLabel: 'Sign In',
    loginSubmitLabel: 'Sign In',
    passwordStrength: {
      containsAtLeast:
        'Must contain at least %d of the following %d types of characters:',
      numbers: 'Numbers (i.e. 0–9)',
      lengthAtLeast: 'Must be at least %d characters in length',
      lowerCase: 'Lower case letters (a–z)',
      upperCase: 'Upper case letters (A–Z)',
    },
  },
  closable: false,
  defaultADUsernameFromEmailPrefix: false,
  avatar: null,
  allowShowPassword: true,
  allowAutocomplete: true,
});

const getElements = (doc) => {
  const properties = {
    innerText: 'innerText',
    none: null,
    textContent: 'textContent',
  };

  // Turning off prettier so the with-ers can be on newlines
  // prettier-ignore
  return {
    appSubLogo: new WrappedElement(doc, 'appSubLogo'),
    auth0RootEmailInput: new WrappedElement(doc, 'auth0', 'input[type=email]'),
    backToSigninButton: new WrappedElement(doc, 'backToSigninV2'),
    emailForm:
      new WrappedElement(doc, 'emailForm')
        .withVisibilityMapping(new VisibilityMapping('isEmailFormVisible')),
    _emailLookupInput:
       new WrappedElement(doc, 'emailInput')
         .withVisibilityMapping(new VisibilityMapping('isEmailFormVisible')),
    _emailLookupInputLabel:
      new WrappedElement(doc, 'emailInputLabel')
        .withVisibilityMapping(new VisibilityMapping('isEmailFormVisible')),
    errorFlash:
      new WrappedElement(doc, 'errorFlash')
        .withVisibilityMapping(new VisibilityMapping('isErrorVisible')),
    forgotPasswordButtonV2: new WrappedElement(doc, 'forgotPasswordButtonV2'),
    forgotPasswordModalPanel:
      new WrappedElement(doc, 'forgotPasswordModalPanel')
        .withVisibilityMapping(new VisibilityMapping('isBackToSignInButtonVisible')),
    heading:
      new WrappedElement(doc, 'heading')
        .withTextMapping(new TextMapping('headingText', properties.innerText)),
    showCompanySignInButton: new WrappedElement(doc, 'showCompanySignInButton'),
    showSignInFormButton: new WrappedElement(doc, 'showSignInFormButton'),
    showSignInFormButtonCompany:
      new WrappedElement(doc, 'showSignInFormButtonCompany'),
    showSignInPanel:
      new WrappedElement(doc, 'signInPanel')
        .withVisibilityMapping(new VisibilityMapping('isShowSignInPanelVisible')),
    showSignInPanelCompany:
      new WrappedElement(doc, 'signInPanelCompany')
        .withVisibilityMapping(new VisibilityMapping('isShowSignInPanelCompanyVisible')),
    showSignUpFormButton: new WrappedElement(doc, 'showSignUpFormButton'),
    showSignUpPanel:
      new WrappedElement(doc, 'signUpPanel')
        .withVisibilityMapping(new VisibilityMapping('isShowSignUpPanelVisible')),
    signInModalPanel:
      new WrappedElement(doc, 'signInModalPanel')
        .withVisibilityMapping(new VisibilityMapping('isSignInModalPanelVisible')),
    subheading:
      new WrappedElement(doc, 'subheading')
        .withTextMapping(new TextMapping('subheadingText', properties.innerText)),
    subcontext:
      new WrappedElement(doc, 'subcontext')
        .withTextMapping(new TextMapping('subcontextText', properties.innerText)),
    auth0WrapperDiv: new WrappedElement(doc, 'auth0')
  };
};
const autofocusInputSelectors = {
  [AUTOFOCUS_CALLBACK.company]: () => [document.getElementById('emailInput')],
  [AUTOFOCUS_CALLBACK.signIn]: () =>
    Array(0)
      .concat(Array.from(document.getElementsByName('email')))
      .concat(Array.from(document.getElementsByName('password'))),
  [AUTOFOCUS_CALLBACK.signUp]: () =>
    Array(0)
      .concat(Array.from(document.getElementsByName('email')))
      .concat(Array.from(document.getElementsByName('password'))),
  [AUTOFOCUS_CALLBACK.forgotPassword]: () =>
    Array.from(document.getElementsByName('email')),
};

const errorLogger = new ErrorLogger(TXID);
const errorHandler = new TopLevelErrorHandler(
  TXID,
  topLevelErrorRenderer(
    new HidableElement(document.getElementById('mainContainer')),
    new HidableElement(document.getElementById('topLevelErrorContainer')),
    new TextContainerElement(document.getElementById('topLevelErrorCode'))
  ),
  errorLogger.logError
);

const addEventListeners = (wrappedElements, loginView, renderCallback) => {
  wrappedElements.forgotPasswordButtonV2.addEventListener(
    'click',
    errorHandler.wrap(async () => {
      await analytics(ELEMENT_CLICKED, {
        ...ELEMENT_CLICKED_PROPERTIES,
        element: 'link',
        label: 'Forgot Password?',
        page: 'Sign-in',
      });

      loginView.forgotPassword().then(renderCallback);
    })
  );

  wrappedElements.backToSigninButton.addEventListener(
    'click',
    errorHandler.wrap(async () => {
      await analytics(ELEMENT_CLICKED, {
        ...ELEMENT_CLICKED_PROPERTIES,
        label: 'Back to Sign In',
        page: 'Forgot Password',
      });

      loginView.signInForm().then(renderCallback);
    })
  );

  wrappedElements.showCompanySignInButton.addEventListener(
    'click',
    errorHandler.wrap(async () => {
      await analytics(ELEMENT_CLICKED, {
        ...ELEMENT_CLICKED_PROPERTIES,
        label: 'Sign In With SSO',
        page: 'Sign-in',
      });

      loginView.emailForm().then(renderCallback);
    })
  );

  wrappedElements.showSignInFormButton.addEventListener(
    'click',
    errorHandler.wrap(async () => {
      await analytics(ELEMENT_CLICKED, {
        ...ELEMENT_CLICKED_PROPERTIES,
        label: 'Sign in',
        page: 'Create Account',
      });

      loginView.signInForm().then(renderCallback);
    })
  );

  wrappedElements.showSignInFormButtonCompany.addEventListener(
    'click',
    errorHandler.wrap(async () => {
      await analytics(ELEMENT_CLICKED, {
        ...ELEMENT_CLICKED_PROPERTIES,
        label: 'Sign in with email and password',
        page: 'Sign In With SSO',
      });

      loginView.signInForm().then(renderCallback);
    })
  );

  wrappedElements.showSignUpFormButton.addEventListener(
    'click',
    errorHandler.wrap(async () => {
      await analytics(ELEMENT_CLICKED, {
        ...ELEMENT_CLICKED_PROPERTIES,
        element: 'link',
        label: 'Create an account',
        page: 'Sign-in',
      });

      loginView.signUpForm().then(renderCallback);
    })
  );
};

const showLogin = () => {
  const config = initConfiguration();
  const options = initOptions(config);
  const lockWrapper = new LockWrapper(config, options, Auth0Lock);
  const wrappedElements = getElements(document);
  const appName = config.dict.signin.title || '';
  const shortAppName = appName.split(' (')[0];
  const loginView = LoginView(
    shortAppName,
    new EmailFirst(EMAIL_LOOKUP_URL),
    maintenanceModeEnabled,
    TXID,
    errorLogger.logError
  );

  const renderer = new Renderer(
    lockWrapper,
    loginView.signInForm,
    wrappedElements,
    new Autofocuser(autofocusInputSelectors),
    config.auth0Domain,
    errorHandler.wrap
  );

  addSubLogo(wrappedElements.appSubLogo.element, appName, ASSET_VERSIONED_URL);

  overrideSmartDollarCreateAccount(appName, wrappedElements);

  const requestedScreen =
    QUERY_PARAM_TO_LOCK_SCREEN_MAP[config.extraParams.display];

  const renderCallback = (vs) => renderer.render(vs);

  // The following statement will execute asynchronously.
  errorHandler.invoke(() =>
    loginView
      .init(config.extraParams.login_hint, requestedScreen)
      .then(renderCallback)
  );

  const submitHandler = errorHandler.wrap(async (target) => {
    let email = target.elements.email.value;
    await loginView.onEmailFormSubmit(email).then(renderCallback);
  });

  form(formErrors, wrappedElements.emailForm.element, submitHandler);

  addEventListeners(wrappedElements, loginView, renderCallback);
};

errorHandler.invoke(showLogin);
